import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { getAccessToken } from "../store/actions/auth";
import Loading from "../components/Loading";
import API_BASE_URL from "../config/baseUrl";

const CalendarIcon = () => (
  <svg
    viewBox="64 64 896 896"
    focusable="false"
    width="1em"
    height="1em"
    fill="currentColor"
  >
    <path d="M880 184H712v-64c0-4.4-3.6-8-8-8h-56c-4.4 0-8 3.6-8 8v64H384v-64c0-4.4-3.6-8-8-8h-56c-4.4 0-8 3.6-8 8v64H144c-17.7 0-32 14.3-32 32v664c0 17.7 14.3 32 32 32h736c17.7 0 32-14.3 32-32V216c0-17.7-14.3-32-32-32zm-40 656H184V460h656v380zM184 392V256h128v48c0 4.4 3.6 8 8 8h56c4.4 0 8-3.6 8-8v-48h256v48c0 4.4 3.6 8 8 8h56c4.4 0 8-3.6 8-8v-48h128v136H184z"></path>
  </svg>
);

const SwapRightIcon = () => (
  <svg
    viewBox="0 0 1024 1024"
    focusable="false"
    width="1em"
    height="1em"
    fill="currentColor"
  >
    <path d="M873.1 596.2l-164-208A32 32 0 00684 376h-64.8c-6.7 0-10.4 7.7-6.3 13l144.3 183H152c-4.4 0-8 3.6-8 8v60c0 4.4 3.6 8 8 8h695.9c26.8 0 41.7-30.8 25.2-51.8z"></path>
  </svg>
);

const LeftArrowIcon = () => (
  <svg
    viewBox="64 64 896 896"
    focusable="false"
    width="1em"
    height="1em"
    fill="currentColor"
  >
    <path d="M724 218.3V141c0-6.7-7.7-10.4-12.9-6.3L260.3 486.8a31.86 31.86 0 000 50.3l450.8 352.1c5.3 4.1 12.9.4 12.9-6.3v-77.3c0-4.9-2.3-9.6-6.1-12.6l-360-281 360-281.1c3.8-3 6.1-7.7 6.1-12.6z"></path>
  </svg>
);

const RightArrowIcon = () => (
  <svg
    viewBox="64 64 896 896"
    focusable="false"
    width="1em"
    height="1em"
    fill="currentColor"
  >
    <path d="M765.7 486.8L314.9 134.7A7.97 7.97 0 00302 141v77.3c0 4.9 2.3 9.6 6.1 12.6l360 281.1-360 281.1c-3.9 3-6.1 7.7-6.1 12.6V883c0 6.7 7.7 10.4 12.9 6.3l450.8-352.1a31.96 31.96 0 000-50.4z"></path>
  </svg>
);

const formatApiDate = function (date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return year + "-" + month + "-" + day;
};

const getDefaultDates = function () {
  const today = new Date();
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

  return {
    startDate: formatApiDate(oneMonthAgo),
    endDate: formatApiDate(today),
  };
};

const fetchProfitLoss = async function (startDate, endDate) {
  try {
    const token = getAccessToken();
    if (!token) return { success: false, error: "No access token found" };

    const response = await axios.post(
      API_BASE_URL + "/ant-pro/ledger-profit-loss",
      { startDate: startDate, endDate: endDate },
      { headers: { Authorization: "Bearer " + token } },
    );

    if (response.data.status === false) {
      return {
        success: false,
        error: response.data.message || "Failed to fetch",
      };
    }

    return { success: true, data: response.data.data || [] };
  } catch (err) {
    return {
      success: false,
      error: err.response?.data?.message || "Failed to fetch",
    };
  }
};

const ProfitLossPage = () => {
  const navigate = useNavigate();
  const defaultDates = getDefaultDates();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profitLossData, setProfitLossData] = useState([]);
  const [selectedReportType, setSelectedReportType] = useState("all");
  const [startDate, setStartDate] = useState(defaultDates.startDate);
  const [endDate, setEndDate] = useState(defaultDates.endDate);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  const loadData = async function () {
    setLoading(true);
    setError(null);

    const result = await fetchProfitLoss(startDate, endDate);

    if (result.success) {
      setProfitLossData(result.data);
      setCurrentPage(1);
    } else {
      setError(result.error);
      setProfitLossData([]);
    }

    setLoading(false);
  };

  useEffect(function () {
    loadData();
  }, []);

  const filteredData = useMemo(function () {
    if (selectedReportType === "casino") {
      return profitLossData.filter(function (item) {
        return item.eventName === "Casino Ledger";
      });
    }
    if (selectedReportType === "sport") {
      return profitLossData.filter(function (item) {
        return item.eventName !== "Casino Ledger";
      });
    }
    return profitLossData;
  }, [profitLossData, selectedReportType]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = filteredData.slice(startIndex, endIndex);

  const handlePageChange = function (page) {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  useEffect(
    function () {
      if (currentPage > totalPages && totalPages > 0) {
        setCurrentPage(1);
      }
    },
    [currentPage, totalPages],
  );

  const calculateTotal = function () {
    let totalCredit = 0;
    let totalDebit = 0;

    currentData.forEach(function (item) {
      totalCredit += item.credit || 0;
      totalDebit += item.debit || 0;
    });

    return {
      credit: totalCredit,
      debit: totalDebit,
      net: totalCredit + totalDebit,
    };
  };

  const totals = calculateTotal();

  return (
    <div className="w-full">
      <div className="flex justify-center">
        <div className="w-full max-w-7xl px-2">
          <div className="bg-white rounded shadow border border-gray-200">
            <div className="bg-[#0F2327] px-3 sm:px-5 py-3 flex items-center justify-between flex-wrap gap-2">
              <span className="text-base sm:text-lg md:text-2xl text-white font-normal capitalize truncate flex-1">
                Profit Loss
              </span>
              <button
                onClick={function () {
                  navigate(-1);
                }}
                className="bg-blue-500 text-white px-3 sm:px-4 py-1 hover:bg-blue-600 text-sm sm:text-base whitespace-nowrap"
                style={{ borderRadius: 0 }}
              >
                Back
              </button>
            </div>

            <form
              onSubmit={function (e) {
                e.preventDefault();
                loadData();
              }}
              className="flex flex-col lg:flex-row lg:justify-between lg:items-center px-3 sm:px-4 py-3 gap-3"
            >
              <div
                className="flex items-center border border-gray-300 bg-white w-full lg:w-auto"
                style={{ borderRadius: 0 }}
              >
                <div className="flex-1 lg:flex-none px-2 py-2 min-w-0">
                  <input
                    type="date"
                    value={startDate}
                    onChange={function (e) {
                      setStartDate(e.target.value);
                    }}
                    className="w-full text-xs sm:text-sm focus:outline-none"
                  />
                </div>
                <div className="px-2 text-gray-400 flex-shrink-0">
                  <SwapRightIcon />
                </div>
                <div className="flex-1 lg:flex-none px-2 py-2 min-w-0">
                  <input
                    type="date"
                    value={endDate}
                    onChange={function (e) {
                      setEndDate(e.target.value);
                    }}
                    className="w-full text-xs sm:text-sm focus:outline-none"
                  />
                </div>
                <div className="px-2 text-gray-400 flex-shrink-0">
                  <CalendarIcon />
                </div>
              </div>

              <div className="flex items-center gap-3 flex-wrap">
                <select
                  className="border border-gray-300 px-3 py-2 text-xs sm:text-sm bg-white"
                  value={selectedReportType}
                  onChange={function (e) {
                    setSelectedReportType(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="all">All</option>
                  <option value="sport">Sport</option>
                  <option value="casino">Casino</option>
                </select>
                <button
                  type="submit"
                  className="bg-blue-500 text-white px-3 sm:px-4 py-2 hover:bg-blue-600 text-xs sm:text-sm"
                >
                  Submit
                </button>
              </div>
            </form>

            <div className="overflow-x-auto">
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loading />
                </div>
              ) : error ? (
                <div className="p-4 text-red-500 text-center text-sm sm:text-base">
                  {error}
                </div>
              ) : (
                <table className="w-full min-w-full border-collapse text-xs sm:text-sm border border-gray-300">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="p-2 border border-gray-300 text-left font-medium whitespace-nowrap">
                        S.No
                      </th>
                      <th className="p-2 border border-gray-300 text-left font-medium whitespace-nowrap">
                        Date
                      </th>
                      <th className="p-2 border border-gray-300 text-left font-medium">
                        Event Name
                      </th>
                      <th className="p-2 border border-gray-300 text-left font-medium whitespace-nowrap">
                        Debit
                      </th>
                      <th className="p-2 border border-gray-300 text-left font-medium whitespace-nowrap">
                        Credit
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentData.length === 0 ? (
                      <tr>
                        <td
                          colSpan="5"
                          className="p-8 text-center text-gray-500 bg-gray-50"
                        >
                          No profit/loss data found
                        </td>
                      </tr>
                    ) : (
                      <>
                        {currentData.map(function (item, index) {
                          const rowBgClass =
                            index % 2 === 0 ? "" : "bg-gray-50";

                          return (
                            <tr key={index} className={rowBgClass}>
                              <td className="p-2 border border-gray-300">
                                <span className="whitespace-nowrap px-1 sm:px-2 block">
                                  {startIndex + index + 1}
                                </span>
                              </td>
                              <td className="p-2 border border-gray-300">
                                <span className="whitespace-nowrap px-1 sm:px-2 block">
                                  {item.date || "-"}
                                </span>
                              </td>
                              <td className="p-2 border border-gray-300">
                                <span className="px-1 sm:px-2 block break-words">
                                  {item.eventName || "-"}
                                </span>
                              </td>
                              <td className="p-2 border border-gray-300">
                                <span
                                  className={
                                    item.debit < 0
                                      ? "text-red-600 font-semibold"
                                      : "text-black"
                                  }
                                >
                                  {item.debit || 0}
                                </span>
                              </td>
                              <td className="p-2 border border-gray-300">
                                <span
                                  className={
                                    item.credit > 0
                                      ? "text-green-600 font-semibold"
                                      : "text-black"
                                  }
                                >
                                  {item.credit || 0}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                        <tr className="bg-blue-100 font-bold">
                          <td className="p-2 border border-gray-300">
                            <span className="px-1 sm:px-2 block">Total</span>
                          </td>
                          <td className="p-2 border border-gray-300"></td>
                          <td className="p-2 border border-gray-300">
                            <span
                              className={
                                totals.net < 0
                                  ? "text-red-600 font-semibold"
                                  : "text-green-600 font-semibold"
                              }
                            >
                              Total Pnl: {totals.net.toFixed(2)}
                            </span>
                          </td>
                          <td className="p-2 border border-gray-300">
                            <span className="text-red-600 font-semibold">
                              {totals.debit.toFixed(2)}
                            </span>
                          </td>
                          <td className="p-2 border border-gray-300">
                            <span className="text-green-600 font-semibold">
                              {totals.credit.toFixed(2)}
                            </span>
                          </td>
                        </tr>
                      </>
                    )}
                  </tbody>
                </table>
              )}
            </div>

            {filteredData.length > itemsPerPage && (
              <div className="flex justify-end p-3">
                <ul className="flex items-center gap-1">
                  <li>
                    <button
                      onClick={function () {
                        handlePageChange(currentPage - 1);
                      }}
                      disabled={currentPage === 1}
                      className="px-2 py-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                    >
                      <LeftArrowIcon />
                    </button>
                  </li>
                  {Array.from(
                    { length: Math.min(totalPages, 5) },
                    function (_, i) {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }

                      return (
                        <li key={pageNum}>
                          <button
                            onClick={function () {
                              handlePageChange(pageNum);
                            }}
                            className={
                              "px-3 py-1 rounded text-xs sm:text-sm " +
                              (currentPage === pageNum
                                ? "bg-blue-500 text-white"
                                : "border border-gray-300 hover:bg-gray-100")
                            }
                          >
                            {pageNum}
                          </button>
                        </li>
                      );
                    },
                  )}
                  <li>
                    <button
                      onClick={function () {
                        handlePageChange(currentPage + 1);
                      }}
                      disabled={currentPage === totalPages || totalPages === 0}
                      className="px-2 py-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                    >
                      <RightArrowIcon />
                    </button>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfitLossPage;
